import AttachmentWellViewState from '../schema/AttachmentWellViewState';
import getAttachmentViewStateFromAttachmentWell from '../utils/getAttachmentViewStateFromAttachmentWell';
import { PerformanceDatapoint } from 'owa-analytics';
import {
    AttachmentFullViewState,
    saveToCloudPreProcess,
    setActionCompletePercent,
    startFakeProgressIndicator,
    stopFakeProgressIndicator,
} from 'owa-attachment-full-data';
import { saveAttachmentsToCloud } from 'owa-attachment-savetocloud';
import { ClientAttachmentId, ClientItemId } from 'owa-client-ids';
import { NotificationCallback, NotificationSubscription } from 'owa-notification';
import {
    getSaveAllToCloudNotificationHandler,
    getSaveAllToCloudFailureHandler,
} from '../utils/getSaveAllToCloudNotificationHandler';

export default async function saveAllToCloud(
    attachmentWell: AttachmentWellViewState,
    parentItemId: ClientItemId,
    completeHandler: (operationFailed: boolean) => void
): Promise<[string, NotificationSubscription, NotificationCallback, ClientAttachmentId[]]> {
    return new Promise<
        [string, NotificationSubscription, NotificationCallback, ClientAttachmentId[]]
    >((resolve, reject) => {
        const attachmentsToSaveToCloud: ClientAttachmentId[] = [];
        let operationStatus: { operationFailed: boolean; error: string } = {
            operationFailed: false,
            error: undefined,
        };
        const datapoint: PerformanceDatapoint = new PerformanceDatapoint(
            'AttachmentSaveAllToCloud'
        );
        preProcessAllAttachments(attachmentWell, attachmentsToSaveToCloud);

        const notificationHandler = getSaveAllToCloudNotificationHandler(
            attachmentsToSaveToCloud,
            operationStatus,
            datapoint,
            attachmentWell.totalClassicAttachmentSize,
            (clientAttachmentId: ClientAttachmentId) =>
                getAttachmentViewStateFromAttachmentWell(attachmentWell, clientAttachmentId),
            completeHandler,
            stopFakeProgressIndicator
        );

        const serviceCallFailureCallback = getSaveAllToCloudFailureHandler(
            attachmentsToSaveToCloud,
            datapoint,
            attachmentWell.totalClassicAttachmentSize,
            (clientAttachmentId: ClientAttachmentId) =>
                getAttachmentViewStateFromAttachmentWell(attachmentWell, clientAttachmentId),
            completeHandler,
            stopFakeProgressIndicator
        );

        const saveAllToCloudTriggeredCallback = (
            operationId: string,
            notificationSubscription: NotificationSubscription,
            notificationCallback: NotificationCallback
        ) => {
            resolve([
                operationId,
                notificationSubscription,
                notificationCallback,
                attachmentsToSaveToCloud,
            ]);
        };

        saveAttachmentsToCloud(
            null,
            parentItemId,
            notificationHandler,
            serviceCallFailureCallback,
            saveAllToCloudTriggeredCallback
        );
    });
}

function preProcessAllAttachments(
    attachmentWell: AttachmentWellViewState,
    attachmentsToSaveToCloud: ClientAttachmentId[]
) {
    const estimatedCompleteTimeInMS: number = attachmentWell.totalClassicAttachmentSize / 256;
    preProcessAttachments(
        attachmentWell.docViewAttachments,
        attachmentsToSaveToCloud,
        estimatedCompleteTimeInMS
    );
    preProcessAttachments(
        attachmentWell.imageViewAttachments,
        attachmentsToSaveToCloud,
        estimatedCompleteTimeInMS
    );
}

function preProcessAttachments(
    attachments: AttachmentFullViewState[],
    attachmentsToSaveToCloud: ClientAttachmentId[],
    estimatedCompleteTimeInMS: number
) {
    attachments.forEach(attachment => {
        attachmentsToSaveToCloud.push(attachment.attachmentId);
        saveToCloudPreProcess(attachment);
        startFakeProgressIndicator(
            estimatedCompleteTimeInMS,
            0.1,
            (completePercent: number) => {
                setActionCompletePercent(attachment, completePercent);
            },
            attachment.attachmentId
        );
    });
}
